import {
  Badge,
  Card,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Search } from "lucide-react";

import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api/client";
import { formatDate, formatDateTime } from "@/lib/format";

const STATUS_COLOR: Record<string, string> = {
  trial: "yellow",
  active: "green",
  overdue: "orange",
  blocked: "red",
  cancelled: "gray",
  pending: "blue",
  paid: "green",
  partial: "yellow",
};

interface AdminBilling {
  plans: Array<{
    clinic_id: string;
    clinic_name: string;
    plan_name: string;
    monthly_fee: number;
    currency: string;
    status: string;
    trial_until: string | null;
    paid_until: string | null;
  }>;
  invoices: Array<{
    id: string;
    clinic_id: string;
    clinic_name: string;
    period_from: string;
    period_to: string;
    amount: number;
    currency: string;
    paid_amount: number;
    paid_at: string | null;
    method: string | null;
    status: string;
    created_at: string;
  }>;
}

export function AdminBillingPage() {
  const [search, setSearch] = useState("");
  const [debSearch] = useDebouncedValue(search, 250);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-all-billing"],
    queryFn: async () => {
      const { data } = await api.get<AdminBilling>("/admin/billing");
      return data;
    },
  });

  const plans = (data?.plans ?? []).filter((p) => {
    if (!debSearch.trim()) return true;
    return p.clinic_name.toLowerCase().includes(debSearch.toLowerCase());
  });
  const invoices = (data?.invoices ?? []).filter((i) => {
    if (!debSearch.trim()) return true;
    return i.clinic_name.toLowerCase().includes(debSearch.toLowerCase());
  });

  return (
    <Stack gap="lg">
      <PageHeader title="Billing — barcha klinikalar" />

      <Card padding="md">
        <TextInput
          rightSection={<Search size={18} strokeWidth={2} />}
          placeholder="Klinika nomi..."
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </Card>

      <Card padding={0} radius="xl">
        <Group p="lg" pb="md">
          <Title order={5} fw={700}>To'lov rejalari</Title>
        </Group>
        <Table.ScrollContainer minWidth={900}>
          <Table verticalSpacing="md" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Klinika</Table.Th>
                <Table.Th>Reja</Table.Th>
                <Table.Th>Oylik to'lov</Table.Th>
                <Table.Th>Holat</Table.Th>
                <Table.Th>Trial muddati</Table.Th>
                <Table.Th>To'langan muddati</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && (
                <Table.Tr><Table.Td colSpan={6}><Loader size="sm" /></Table.Td></Table.Tr>
              )}
              {plans.map((p) => (
                <Table.Tr key={p.clinic_id}>
                  <Table.Td><Text size="sm" fw={600}>{p.clinic_name}</Text></Table.Td>
                  <Table.Td><Badge variant="light" radius="sm">{p.plan_name}</Badge></Table.Td>
                  <Table.Td>
                    <Text fw={600}>
                      {Number(p.monthly_fee).toLocaleString()} {p.currency}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={STATUS_COLOR[p.status] ?? "gray"} radius="sm">
                      {p.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td><Text size="xs">{p.trial_until ? formatDate(p.trial_until) : "—"}</Text></Table.Td>
                  <Table.Td><Text size="xs">{p.paid_until ? formatDate(p.paid_until) : "—"}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      <Card padding={0} radius="xl">
        <Group p="lg" pb="md">
          <Title order={5} fw={700}>Invoyslar</Title>
        </Group>
        <Table.ScrollContainer minWidth={1000}>
          <Table verticalSpacing="md" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Klinika</Table.Th>
                <Table.Th>Davr</Table.Th>
                <Table.Th>Summa</Table.Th>
                <Table.Th>To'langan</Table.Th>
                <Table.Th>Holat</Table.Th>
                <Table.Th>To'lov sanasi</Table.Th>
                <Table.Th>Usul</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {invoices.length === 0 && !isLoading && (
                <Table.Tr><Table.Td colSpan={7}>
                  <Text c="dimmed" ta="center" py="md">Hech qanday invoys yo'q</Text>
                </Table.Td></Table.Tr>
              )}
              {invoices.map((i) => (
                <Table.Tr key={i.id}>
                  <Table.Td>
                    <Badge variant="light" color="brand" radius="sm">{i.clinic_name}</Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{formatDate(i.period_from)} – {formatDate(i.period_to)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text fw={600}>{Number(i.amount).toLocaleString()} {i.currency}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text>{Number(i.paid_amount).toLocaleString()} {i.currency}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={STATUS_COLOR[i.status] ?? "gray"} radius="sm">
                      {i.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">{i.paid_at ? formatDateTime(i.paid_at) : "—"}</Text>
                  </Table.Td>
                  <Table.Td><Text size="xs">{i.method ?? "—"}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </Stack>
  );
}
